import React, { useMemo } from 'react'
import { TFunction } from 'i18next'
import { useNewTranslation } from 'hooks'
import Icon from 'components/Icon/Icon'
import PhotoBlockImage from 'icons/PhotoBlock.png'
import { BLOCK_TYPE as CUSTOM_TASK_BLOCK_TYPE } from 'state/tasks'
import { BLOCK_TYPE as REGISTRATION_BLOCK_TYPE } from 'state/registrations'
import MenuItem from 'particles/MenuItem'

export enum BLOCK_GROUP {
    CUSTOM,
    REGISTRATION,
}

export type BlockType = CUSTOM_TASK_BLOCK_TYPE | REGISTRATION_BLOCK_TYPE

type BlockMenuItem = {
    type: BlockType
    title: string
    description: string
    icon: JSX.Element
}

type BlockMenuGroup = {
    title: string
    blocks: BlockMenuItem[]
}

const getMenusForCustomTask = (t: TFunction, showNonSubmissionBlockOnly = false): BlockMenuGroup[] => {
    const menus = [
        {
            title: t('campaignEditor.customAndRegistrationField.descriptionForPartipostersMenuHeader'),
            blocks: [
                {
                    type: CUSTOM_TASK_BLOCK_TYPE.TEXT,
                    title: t('campaignEditor.customAndRegistrationField.textFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.textFieldMenuDescription'),
                    icon: <Icon.TextBlockIcon />,
                },
                {
                    type: CUSTOM_TASK_BLOCK_TYPE.PHOTO_CAROUSEL,
                    title: t('campaignEditor.customAndRegistrationField.uploadPhotoFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.photoFieldMenuDescription'),
                    icon: <img alt="photo block" src={PhotoBlockImage} />,
                },
            ],
        },
    ]

    if (!showNonSubmissionBlockOnly) {
        menus.push({
            title: t('campaignEditor.customAndRegistrationField.thingsRequiredHeader'),
            blocks: [
                {
                    type: CUSTOM_TASK_BLOCK_TYPE.PHOTO_SUBMISSION,
                    title: t('campaignEditor.customAndRegistrationField.imageUploadHeader'),
                    description: t('campaignEditor.customAndRegistrationField.imageUploadMenuDescription'),
                    icon: <Icon.PhotoIcon />,
                },
                {
                    type: CUSTOM_TASK_BLOCK_TYPE.URL_SUBMISSION,
                    title: t('campaignEditor.customAndRegistrationField.urlFieldHeader'),
                    description: t('menu.campaignEditor.customTask.selectUrlFieldSubtext'),
                    icon: <Icon.LinkIcon />,
                },
                {
                    type: CUSTOM_TASK_BLOCK_TYPE.TEXT_SUBMISSION,
                    title: t('campaignEditor.customAndRegistrationField.openEndedFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.openEndedMenuDescription'),
                    icon: <Icon.FreeInputIcon />,
                },
            ],
        })
    }

    return menus
}

const getMenusForRegistration = (t: TFunction, showNonSubmissionBlockOnly = false): BlockMenuGroup[] => {
    const menus = [
        {
            title: t('campaignEditor.customAndRegistrationField.descriptionForPartipostersMenuHeader'),
            blocks: [
                {
                    type: REGISTRATION_BLOCK_TYPE.TEXT,
                    title: t('campaignEditor.customAndRegistrationField.textFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.textFieldMenuDescription'),
                    icon: <Icon.TextBlockIcon />,
                },
                {
                    type: REGISTRATION_BLOCK_TYPE.PHOTOS,
                    title: t('campaignEditor.customAndRegistrationField.photoFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.photoFieldMenuDescription'),
                    icon: <img alt="photo block" src={PhotoBlockImage} />,
                },
            ],
        },
    ]

    if (!showNonSubmissionBlockOnly) {
        menus.push({
            title: t('campaignEditor.customAndRegistrationField.thingsRequiredHeader'),
            blocks: [
                {
                    type: REGISTRATION_BLOCK_TYPE.QUESTION,
                    title: t('campaignEditor.customAndRegistrationField.multipleChoiceFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.multipleChoiceMenuDescription'),
                    icon: <Icon.MultipleChoiceIcon />,
                },
                {
                    type: REGISTRATION_BLOCK_TYPE.PHOTO_SUBMISSION,
                    title: t('campaignEditor.customAndRegistrationField.imageUploadFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.imageUploadMenuDescription'),
                    icon: <Icon.PhotoIcon />,
                },
                {
                    type: REGISTRATION_BLOCK_TYPE.URL_SUBMISSION,
                    title: t('campaignEditor.customAndRegistrationField.urlFieldHeader'),
                    description: t('menu.campaignEditor.customTask.selectUrlFieldSubtext'),
                    icon: <Icon.LinkIcon />,
                },
                {
                    type: REGISTRATION_BLOCK_TYPE.TEXT_SUBMISSION,
                    title: t('campaignEditor.customAndRegistrationField.openEndedFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.openEndedMenuDescription'),
                    icon: <Icon.FreeInputIcon />,
                },
                {
                    type: REGISTRATION_BLOCK_TYPE.NUMBER_SUBMISSION,
                    title: t('campaignEditor.customAndRegistrationField.numberFieldHeader'),
                    description: t('campaignEditor.customAndRegistrationField.numberFieldMenuDescription'),
                    icon: <Icon.NumberIcon />,
                },
            ],
        })
    }

    return menus
}

type BlockMenuProps = {
    onSelect?: (blockType: BlockType) => void
    blockGroup: BLOCK_GROUP
    showNonSubmissionBlockOnly?: boolean
}
const BlockMenu: React.FC<BlockMenuProps> = React.memo(function BlockMenu({
    onSelect,
    blockGroup,
    showNonSubmissionBlockOnly = false,
}) {
    const [t] = useNewTranslation()
    const menus = useMemo(
        () =>
            blockGroup === BLOCK_GROUP.CUSTOM
                ? getMenusForCustomTask(t, showNonSubmissionBlockOnly)
                : getMenusForRegistration(t, showNonSubmissionBlockOnly),
        [t, blockGroup],
    )

    const handleAddBlock = (blockType: BlockType): void => {
        if (onSelect) onSelect(blockType)
    }

    return (
        <>
            {menus.map(group => (
                <div className="menu-section" key={group.title}>
                    {group.title && <MenuItem.Title text={group.title} size="s" case="uppercase" />}
                    {group.blocks.map((block, sectionIndex) => (
                        <MenuItem.Img
                            key={`menu-item-${sectionIndex}`}
                            title={block.title}
                            description={block.description}
                            img={block.icon}
                            onClick={(): void => handleAddBlock(block.type)}
                            tabIndex={sectionIndex}
                        />
                    ))}
                </div>
            ))}
        </>
    )
})

export default BlockMenu
